import { useState, useRef, useEffect } from "react";
import { UserCheck, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Compact reassign dropdown for a single doc (quote or inventory).
 * On selection, calls POST /:resource/:id/assign?mover_id=... and triggers onReassigned().
 * `operators` is the full list with workload counts; current operator is excluded.
 */
export function ReassignMenu({ docId, docType, currentOperatorId, operators, onReassigned }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  const candidates = (operators || [])
    .filter(op => op.id !== currentOperatorId && op.is_active && op.role !== "OWNER")
    .sort((a, b) => a.total_count - b.total_count); // meno carico in cima

  const handleAssign = async (newOperatorId) => {
    setSubmitting(true);
    try {
      const resource = docType === "inventory" ? "inventories" : "quotes";
      await api.post(`/${resource}/${docId}/assign?mover_id=${encodeURIComponent(newOperatorId)}`);
      toast.success("Lavoro riassegnato");
      setOpen(false);
      onReassigned?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nella riassegnazione");
    } finally {
      setSubmitting(false);
    }
  };

  if (candidates.length === 0) return null;

  return (
    <div className="relative shrink-0" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        disabled={submitting}
        data-testid={`reassign-trigger-${docType}-${docId}`}
        className="px-1.5 py-1 rounded-sm border border-zinc-200 bg-white hover:bg-blue-50/50 text-zinc-600 hover:text-[#002FA7] transition flex items-center disabled:opacity-50"
        title="Riassegna a un altro operatore"
        aria-label="Riassegna a un altro operatore"
      >
        <UserCheck className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          data-testid={`reassign-menu-${docType}-${docId}`}
          className="absolute right-0 top-full mt-1 z-20 min-w-[240px] bg-white border border-zinc-200 rounded-sm shadow-lg overflow-hidden"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-400 font-semibold border-b border-zinc-100 bg-zinc-50 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Riassegna a
          </div>
          <div className="max-h-[280px] overflow-y-auto">
            {candidates.map(op => (
              <button
                key={op.id}
                type="button"
                onClick={() => handleAssign(op.id)}
                disabled={submitting}
                data-testid={`reassign-option-${docType}-${docId}-${op.id}`}
                className="w-full text-left px-3 py-2 hover:bg-blue-50/50 transition flex items-center justify-between gap-3 text-sm border-b border-zinc-50 last:border-b-0 disabled:opacity-50"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zinc-900 truncate">{op.name || "Senza nome"}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{op.role_name || "Operatore"}</div>
                </div>
                <div className="text-[10px] text-zinc-500 shrink-0 font-mono">
                  <span className="text-zinc-900 font-semibold">{op.total_count}</span> lavori
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
